import type { HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';
import { MONO_LABEL_TYPE } from './MonoLabel';

/**
 * The one way to render a status or category chip (design-grammar.md §3, #719).
 *
 * WHY A PRIMITIVE. Chip color used to be a hand-written utility recipe —
 * `bg-status-ok text-status-ok-fg` plus, in principle, a `dark:` pairing. In
 * practice 284 of 306 raw palette utilities shipped with no `dark:` variant at
 * all, which is how the moderation lane rendered Guardian-flagged chips as
 * pink-on-brown and the "PARENT / SEEKING SUPPORT" tags as invisible. A recipe
 * you retype at each call site is a recipe that is wrong at most call sites.
 *
 * Here the tone name is the only thing a caller chooses; the bg/fg/border
 * triple comes from tokens that are declared in both `:root` and `.dark`, so a
 * chip cannot be born light-mode-only. The per-page tables in `lib/*-display.ts`
 * return these tone NAMES, never class strings.
 *
 * TONES ARE NOT INTERCHANGEABLE. `ok`/`warn`/`danger` say something is
 * healthy / waiting / wrong. The categorical tones say which KIND — they carry
 * no judgement at all. Reaching for `danger` to color a category is the mistake
 * that made rose mean eight different things at once, `seeking_support` and
 * every medical appointment among them; a support-seeking parent is not an
 * error state, and neither is a Tuesday oncology visit. Categories take a
 * categorical tone or `neutral`.
 *
 * Nothing here is indigo: indigo means interactive, and a chip is not a button.
 */
export type ChipTone =
  | 'ok' | 'warn' | 'warn-strong' | 'danger'
  | 'sky' | 'violet' | 'teal' | 'magenta'
  | 'neutral';

/** Tone -> the Tailwind triple. Every entry is exactly three classes from one
 *  token family; mixing families across a row is how a chip loses its border on
 *  a tinted surface. */
const TONE_CLASS: Record<ChipTone, string> = {
  ok:      'bg-status-ok text-status-ok-fg border-status-ok-border',
  warn:    'bg-status-warn text-status-warn-fg border-status-warn-border',
  // The 4th rung of the warn ramp — see index.css (#751).
  'warn-strong': 'bg-status-warn-strong text-status-warn-strong-fg border-status-warn-strong-border',
  danger:  'bg-status-danger text-status-danger-fg border-status-danger-border',
  sky:     'bg-cat-sky text-cat-sky-fg border-cat-sky-border',
  violet:  'bg-cat-violet text-cat-violet-fg border-cat-violet-border',
  teal:    'bg-cat-teal text-cat-teal-fg border-cat-teal-border',
  magenta: 'bg-cat-magenta text-cat-magenta-fg border-cat-magenta-border',
  neutral: 'bg-chip-neutral text-chip-neutral-fg border-chip-neutral-border',
};

/**
 * The border is part of the triple, not decoration: it is what keeps a chip
 * bounded when it sits on a tinted card, and it costs nothing on neutral
 * ground. It is always on — an opt-out would just be the old bug with a prop.
 *
 * Type is `MONO_LABEL_TYPE` (11px), NOT the 10px `` rung the chips
 * being migrated used. Per grammar §3, 10px mono earns the APCA Lc 90 band, and
 * no tinted bg/fg pair reaches Lc 90 in BOTH themes while its hue stays
 * legible — rose tops out near 83 in dark. So a tinted chip is never 10px.
 * At 11px the bar is Lc 75 and every pair here clears it by 6.7 or more.
 */
const BASE_CLASS = 'inline-flex items-center gap-1 rounded-sharp border px-1.5 py-0.5';

/**
 * The solid/emphasis variant (#743) — a saturated fill with on-solid text.
 *
 * Only the three status tones have one: emphasis means "this state is
 * interrupting you", and a category never interrupts. It exists because the
 * tint triple has no member for a saturated background, and the absence is what
 * let the #721 sweep collapse the impersonation banner's fill and text onto the
 * same token, rendering it invisible in both themes.
 *
 * Reach for it only on a surface that must interrupt — a banner, not a badge.
 * "One accent per screen" still applies, and a page of solid chips is a page
 * with no emphasis at all.
 */
const SOLID_TONES = ['ok', 'warn', 'danger'] as const;
export type SolidTone = (typeof SOLID_TONES)[number];

const SOLID_CLASS: Record<SolidTone, string> = {
  ok:     'bg-status-ok-solid text-status-ok-solid-fg border-status-ok-solid',
  warn:   'bg-status-warn-solid text-status-warn-solid-fg border-status-warn-solid',
  danger: 'bg-status-danger-solid text-status-danger-solid-fg border-status-danger-solid',
};

export function isSolidTone(tone: ChipTone): tone is SolidTone {
  return (SOLID_TONES as readonly string[]).includes(tone);
}

export interface StatusChipProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: ChipTone;
  /** `tint` (default) is the everyday chip. `solid` is for emphasis surfaces
   *  and is only available on the status tones. */
  variant?: 'tint' | 'solid';
}

export function StatusChip({
  tone = 'neutral',
  variant = 'tint',
  className,
  children,
  ...rest
}: StatusChipProps) {
  // A categorical tone has no solid pair — falling back to its tint is the
  // right failure here: quieter than intended beats unreadable.
  const toneClass =
    variant === 'solid' && isSolidTone(tone) ? SOLID_CLASS[tone] : TONE_CLASS[tone];

  return (
    <span className={cn(BASE_CLASS, MONO_LABEL_TYPE, toneClass, className)} {...rest}>
      {children}
    </span>
  );
}
